import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent, Label, Button } from '@higgins/ui';
import { VisualSlot } from './visual-slot';
import { RichTextEditor } from './rich-text-editor';
import { SECTION_KEYS, SECTION_LABELS } from '@/lib/types';
import type { SectionKey, ChartType } from '@/lib/types';
import type { ReportVisualsResponse } from '@/lib/api';

type SectionContent = Record<'content' | 'content2' | 'content3', string>;
export type AllSectionContent = Partial<Record<SectionKey, SectionContent>>;

// Chart type per slot — mirrors the visual registry in the API (slot index is 0-based here).
const SLOT_CHART_TYPES: Record<SectionKey, ChartType[]> = {
  exec_summary: ['matrix'],
  safety:       ['kpi', 'bar', 'kpi', 'bar', 'kpi', 'bar'],
  customer:     ['kpi', 'kpi', 'kpi', 'kpi'],
  sales:        ['kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'matrix'],
  operations:   ['kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi', 'kpi'],
  financial:    ['kpi', 'kpi', 'kpi', 'matrix'],
};

// Explicit grid class overrides where the default count-based logic doesn't suit the layout.
const SECTION_GRID_COLS: Partial<Record<SectionKey, string>> = {
  safety:   'grid-cols-6', // KPI (1 col) + bar (5 cols) pairs
  customer: 'grid-cols-4', // 4 KPI cards in a row
  sales:      'grid-cols-3', // 6 KPIs (2 rows of 3) + full-width matrix
  operations: 'grid-cols-4', // 11 KPIs — 3 rows (4+4+3)
  financial:  'grid-cols-3', // 3 KPIs on row 1, matrix full-width on row 2
};

// Slots that should span extra columns within their section grid.
// Key: `${sectionKey}/${slotIndex}` (0-based index), value: Tailwind col-span class.
const SLOT_COL_SPANS: Record<string, string> = {
  'safety/1': 'col-span-5', // WSRs x Month bar
  'safety/3': 'col-span-5', // Incidents LM bar
  'safety/5': 'col-span-5', // Incidents YTD bar
  'sales/6':     'col-span-3', // Rolling Snapshot Matrix — full width
  'financial/3': 'col-span-3', // Rolling Snapshot Matrix — full width
};

// Slot count per section — exec=1, safety=6, customer=4, sales=7, ops=11, financial=4
const SECTION_SLOT_COUNTS: Record<SectionKey, number> = {
  exec_summary: 1,
  safety: 6,
  customer: 4,
  sales: 7,
  operations: 11,
  financial: 4,
};

// Friendly slot names — display labels for each visual slot, per section
const SLOT_DISPLAY_NAMES: Record<SectionKey, string[]> = {
  exec_summary: ['KPI Summary', 'MTD / YTD Overview'],
  safety: ['WSRs YTD', 'WSRs x Month', 'WSRAs MTD', 'Incidents LM', 'WSRAs YTD', 'Incidents YTD'],
  customer: ['Responses YTD', 'CSAT Av YTD', 'CSAT % YTD', 'NPS Av YTD'],
  sales: ['Quotes YTD', 'Sales YTD', 'Strike Rate YTD', 'Quotes MTD', 'Sales MTD', 'Strike Rate MTD', 'Rolling Snapshot'],
  operations: ['GP MTD', 'GP%', 'GP YTD', 'GP% YTD', 'Net Underclaims (Ex Maint)', 'Net Underclaims % Rev', 'Gross Underclaims (Ex Maint)', 'Gross Underclaims % Rev', 'Maintenance', 'WSRAs MTD', 'WSRAs YTD'],
  financial: ['Earned Revenue', '60+ Debtors', 'NPBIT YTD LM', 'Rolling Snapshot'],
};

const SECTION_FIELDS: Record<SectionKey, { label: string; field: 'content' | 'content2' | 'content3'; placeholder?: string; minHeightClass?: string }[]> = {
  exec_summary: [
    { label: 'Commentary', field: 'content', placeholder: 'Summarise the month overall — highlights, key themes, and anything that needs attention at a senior level.' },
    { label: 'Shout Outs', field: 'content2', placeholder: 'Recognise individuals or teams for great work this month.' },
  ],
  safety: [
    { label: 'Incident Commentary', field: 'content', placeholder: 'Describe any incidents or near-misses this month, corrective actions taken, and current status.' },
    { label: 'People Movements / Activity', field: 'content2', placeholder: 'Note any new starters, leavers, promotions, or significant people activity this month.' },
  ],
  customer: [{ label: 'CSAT Commentary', field: 'content', placeholder: 'Comment on customer satisfaction trends, any notable feedback received, and actions being taken. Include commentary on any responses giving a CSAT score below 4.' }],
  sales:    [{ label: 'Sales Commentary', field: 'content', placeholder: 'Comment on pipeline activity, key wins or losses, and strike rate trends. Discuss any branches missing targets and the reasons why. Include commentary on forecast sales, key projects that could influence the result, accuracy of forecast data, and any areas of risk.', minHeightClass: 'min-h-[480px]' }],
  operations: [{ label: 'Operations Commentary', field: 'content', placeholder: 'Comment on GP performance, underclaims, and any operational issues this month. Address specific underclaim issues and the actions being taken. Include commentary on maintenance late works — pain points, root causes, and progress.', minHeightClass: 'min-h-[480px]' }],
  financial: [
    { label: 'Financial Commentary', field: 'content', placeholder: 'Comment on revenue, billings, and gross profit performance against budget and forecast.' },
    { label: 'Forecast', field: 'content2', placeholder: 'YTD and MTD performance is covered in the Executive Summary — focus here on the forecast for the next three months and any risks to achieving budgeted amounts for the remainder of the year.' },
    { label: 'Debtors', field: 'content3', placeholder: 'Comment on the 60+ debtors position — key outstanding accounts and recovery actions underway.' },
  ],
};

interface SectionTabsProps {
  sections: AllSectionContent;
  visuals?: ReportVisualsResponse['visuals'];
  visualsLoading?: boolean;
  readOnly?: boolean;
  onContentChange: (
    sectionKey: SectionKey,
    field: 'content' | 'content2' | 'content3',
    value: string,
  ) => void;
  onTabChange?: (sectionKey: SectionKey) => void;
}

export function SectionTabs({
  sections,
  visuals,
  visualsLoading = false,
  readOnly = false,
  onContentChange,
  onTabChange,
}: SectionTabsProps) {
  const [active, setActive] = useState<SectionKey>(SECTION_KEYS[0]);
  const topRef = useRef<HTMLDivElement>(null);

  // Switch section. `scrollUp` returns the user to the top of the section (used by
  // the bottom Prev/Next buttons so they don't have to scroll back to the tab bar).
  const goTo = (key: SectionKey, scrollUp = false) => {
    setActive(key);
    onTabChange?.(key);
    if (scrollUp) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const activeIndex = SECTION_KEYS.indexOf(active);
  const prevKey = activeIndex > 0 ? SECTION_KEYS[activeIndex - 1] : null;
  const nextKey = activeIndex < SECTION_KEYS.length - 1 ? SECTION_KEYS[activeIndex + 1] : null;

  return (
    <div ref={topRef} className="scroll-mt-24">
    <Tabs
      value={active}
      onValueChange={v => goTo(v as SectionKey)}
    >
      <TabsList className="w-full justify-start h-auto flex-wrap gap-1 bg-white/60 border border-slate-200/60 p-1">
        {SECTION_KEYS.map(key => (
          <TabsTrigger key={key} value={key} className="text-xs sm:text-sm">
            {SECTION_LABELS[key]}
          </TabsTrigger>
        ))}
      </TabsList>

      {SECTION_KEYS.map(key => {
        const count     = SECTION_SLOT_COUNTS[key];
        const fields    = SECTION_FIELDS[key];
        const slotNames = SLOT_DISPLAY_NAMES[key];
        const sectionContent = sections[key];

        const gridClass = SECTION_GRID_COLS[key] ?? (
          count === 1 ? 'grid-cols-1'
          : count <= 2 ? 'grid-cols-1 sm:grid-cols-2'
          : 'grid-cols-2 sm:grid-cols-4'
        );

        return (
          <TabsContent key={key} value={key} className="mt-5 space-y-6">
            <div className={`grid gap-3 ${gridClass}`}>
              {Array.from({ length: count }, (_, i) => {
                const colSpan = SLOT_COL_SPANS[`${key}/${i}`];
                return (
                  <VisualSlot
                    key={i}
                    section={key}
                    slot={i + 1}
                    displayName={slotNames[i] ?? `Visual ${i + 1}`}
                    visual={visuals?.[key]?.[i + 1]}
                    loading={visualsLoading}
                    expectedChartType={SLOT_CHART_TYPES[key][i]}
                    className={colSpan}
                  />
                );
              })}
            </div>

            <div className="space-y-5">
              {fields.map(({ label, field, placeholder, minHeightClass }) => (
                <div key={field} className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {label}
                  </Label>
                  <RichTextEditor
                    content={sectionContent?.[field] ?? ''}
                    onChange={value => onContentChange(key, field, value)}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    minHeightClass={minHeightClass}
                  />
                </div>
              ))}
            </div>
          </TabsContent>
        );
      })}

      {/* Bottom section navigation — move between sections without scrolling back to
          the tab bar (see issue #13). Labelled with the adjacent section name. */}
      <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-200/60 pt-4">
        <Button
          variant="outline"
          size="sm"
          disabled={!prevKey}
          onClick={() => prevKey && goTo(prevKey, true)}
          className="gap-1.5"
        >
          <ChevronLeft className="w-4 h-4" />
          {prevKey ? SECTION_LABELS[prevKey] : 'Previous'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!nextKey}
          onClick={() => nextKey && goTo(nextKey, true)}
          className="gap-1.5"
        >
          {nextKey ? SECTION_LABELS[nextKey] : 'Next'}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </Tabs>
    </div>
  );
}
